import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAppLaunch } from '../hooks/useAppLaunch'

interface Project {
  id: string
  project_id: string
  name: string
  description: string | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
}

interface PathAccess {
  id: string
  storage_id: string
  storage: {
    id: string
    storage_id: string
    name: string
    backend_type: string
  }
  path_prefix: string
  permission: 'read' | 'write'
}

interface AppAccess {
  access_id: string
  application: {
    id: string
    app_id: string
    name: string
    description: string | null
    url: string
    is_active: boolean
  }
  granted_at: string | null
}

export function Dashboard() {
  const { launchApp } = useAppLaunch()

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects'),
  })

  const { data: paths, isLoading: pathsLoading } = useQuery({
    queryKey: ['paths'],
    queryFn: () => api.get<PathAccess[]>('/files/access'),
  })

  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: () => api.get<AppAccess[]>('/apps'),
  })

  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl mb-8">Dashboard</h1>

      {/* Projects Section */}
      <section className="mb-12">
        <h2 className="section-label">Your Projects</h2>

        {projectsLoading ? (
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        ) : projects && projects.length > 0 ? (
          <div className="space-y-2">
            {projects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.project_id}`}
                className="card-hover flex items-center justify-between no-underline"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📊</span>
                  <div>
                    <span className="font-medium">{project.name}</span>
                    {project.description && (
                      <span className="text-[var(--color-text-secondary)] ml-2 text-sm">
                        {project.description.slice(0, 60)}{project.description.length > 60 ? '...' : ''}
                      </span>
                    )}
                  </div>
                </div>
                {(project.start_date || project.end_date) && (
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {project.start_date && project.end_date
                      ? `${project.start_date} - ${project.end_date}`
                      : project.start_date
                      ? `From ${project.start_date}`
                      : `Until ${project.end_date}`}
                  </span>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-[var(--color-text-secondary)]">
            No projects available. Contact your administrator for access.
          </p>
        )}
      </section>

      {/* Files Section */}
      <section className="mb-12">
        <h2 className="section-label">Your Files</h2>

        {pathsLoading ? (
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        ) : paths && paths.length > 0 ? (
          <div className="space-y-2">
            {paths.map((path) => (
              <Link
                key={path.id || `${path.storage_id}-${path.path_prefix}`}
                to={`/files?storage=${path.storage_id}&path=${encodeURIComponent(path.path_prefix)}`}
                className="card-hover flex items-center justify-between no-underline"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📁</span>
                  <div>
                    <span className="font-medium">{path.storage.name}</span>
                    <span className="text-[var(--color-text-secondary)]">{path.path_prefix}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs bg-[var(--color-surface)] px-2 py-1 rounded">
                    {path.storage.backend_type}
                  </span>
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {path.permission}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-[var(--color-text-secondary)]">
            No files available. Contact your administrator for access.
          </p>
        )}
      </section>

      {/* Apps Section */}
      <section>
        <h2 className="section-label">Your Apps</h2>

        {appsLoading ? (
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        ) : apps && apps.length > 0 ? (
          <div className="space-y-2">
            {apps.map((app) => (
              <div
                key={app.access_id}
                className="card flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🚀</span>
                  <span>{app.application.name}</span>
                </div>
                <button
                  onClick={() => launchApp(app.access_id)}
                  className="btn btn-secondary text-sm"
                >
                  Launch
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[var(--color-text-secondary)]">
            No apps available. Contact your administrator for access.
          </p>
        )}
      </section>
    </div>
  )
}
